import React from 'react'
import { FieldErrors, SubmitHandler, SubmitErrorHandler } from 'react-hook-form'
import get from 'lodash/get'
import { ControlledPhoneInput } from 'transfix-form/components'
import { Button } from 'transfix-ui/components/Button'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Box } from 'transfix-ui/core'
import StepButtonContainer from '../../../StepButtonContainer'
import { TProvidePhoneNumberSchema } from './ProvidePhoneNumberSchema'
import { useProvidePhoneNumberForm } from './useProvidePhoneNumberForm'

const getErrorMessage = <T extends FieldErrors<TProvidePhoneNumberSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface IProvidePhoneNumberFormProps {
  /**
   * @default {}
   */
  defaultValues?: Partial<TProvidePhoneNumberSchema>
  /**
   * @example (data) => {}
   */
  onSubmitSuccess: SubmitHandler<TProvidePhoneNumberSchema>
  /**
   * @example (errors) => {}
   */
  onSubmitError?: SubmitErrorHandler<TProvidePhoneNumberSchema>
  onCancel?: () => void
  alertMessage?: string | null
}

export const ProvidePhoneNumberForm = ({
  defaultValues = {},
  onSubmitSuccess,
  onSubmitError = () => {},
  alertMessage,
  onCancel,
}: IProvidePhoneNumberFormProps) => {
  const { control, errors, handleSubmit } = useProvidePhoneNumberForm({
    defaultValues,
  })

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  return (
    <form onSubmit={submitHandler} data-test='ProvidePhoneNumberForm'>
      <ControlledPhoneInput
        name='phoneNumber'
        control={control}
        PhoneInputProps={{
          label: 'Mobile Number',
          validationMessage: getErrorMessage(errors, 'phoneNumber'),
          invalid: !!getErrorMessage(errors, 'phoneNumber'),
          dataTest: 'input-phoneNumber',
        }}
      />
      {alertMessage && (
        <Box paddingTop='12px'>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={alertMessage} />
        </Box>
      )}
      <StepButtonContainer>
        <Button onClick={onCancel} color='tertiary'>
          Cancel
        </Button>
        <Button type='submit' color='primary' size='large' data-test='submit-button'>
          Get verification code
        </Button>
      </StepButtonContainer>
    </form>
  )
}
